import { InputHintDiv, LabelHelpInfoPopover, LabelStyles, makeFetchInitialStateForLabelValues, useSetIsLabelSevere } from '@tableau/catalog-ui-temp';
import { getMaxCharValidation } from '@tableau/widgets';
import React from 'react';

import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';

import { Messages } from '../../../codegen/Localize';
import { TextFormatterOrAreaWidget } from '../common/TextFormatterOrAreaWidget';
import { isFormDataValidForSubmission } from '../labels/LabelDialog';
import {
  CERTIFICATION_LABEL_VALUE_DIALOG_MESSAGE_ID,
  checkIsValid,
  DESCRIPTION_MAXIMUM_LENGTH,
  DialogType,
  getLabelValueDialogTitle,
  LabelValueActionDialogOptions,
  LabelValueDialogBodyComponentProps,
  LabelValueDialogBodyProps,
  LabelValueDialogState } from './LabelValueDialogHelper';



export function CertificationOrTriggerWarningDialogBody({ updateDialogState, dialogState}: LabelValueDialogBodyComponentProps): React.ReactElement {
    const {description, category, elevatedDefault, labelValueName} = dialogState;
    const type = convertCategoryToType(category);
    const [SevereWarningContent, isSevere] = useSetIsLabelSevere({ initial: elevatedDefault, isLabelValue: true });
    React.useEffect(() => updateDialogState({ elevatedDefault: isSevere}), [isSevere, updateDialogState]);

    const handleMessageChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) =>
      updateDialogState({ description: e.currentTarget.value }), [updateDialogState]);
    const { message: dialogMessage, valid } = getMaxCharValidation(description ?? '', DESCRIPTION_MAXIMUM_LENGTH);

    return (
      <>
      <LabelHelpInfoPopover dialogType={type} labelValueName={labelValueName} />

      {type === 'warning' && SevereWarningContent }

      <TextFormatterOrAreaWidget
        label={
        <InputHintDiv disabled={false}>
          { Messages.descriptionRequired()}
          </InputHintDiv>}
        onChange={handleMessageChange}
        id={CERTIFICATION_LABEL_VALUE_DIALOG_MESSAGE_ID}
        testId={CERTIFICATION_LABEL_VALUE_DIALOG_MESSAGE_ID}
        disabled={false}
        valid={valid}
        message={dialogMessage}
        style={LabelStyles.markupEditorStyle}
        markup={description}
      />
      </>
    );
  }

export function getCertificationOrTriggerWarningActionDialogOptions(args: {
  initialBodyProps: LabelValueDialogBodyProps;
  initialFormData: LabelValueDialogState;
  doAction: (formData: LabelValueDialogState) => Promise<unknown>;
  dialogType: DialogType;
  }): LabelValueActionDialogOptions {
  const { initialBodyProps, initialFormData, doAction, dialogType } = args;

  return {
    title: getLabelValueDialogTitle(initialFormData.labelValueName),
    fetchInitialState: fetchInitialState(dialogType),
    bodyComponent: CertificationOrTriggerWarningDialogBody,
    initialDialogState: initialFormData,
    initialBodyProps: initialBodyProps,
    actionName: Messages.save(),
    action: (_, dialogState, __) => doAction(dialogState),
    actionDisabled: (_, dialogState, fetchedState) => !isFormDataValidForSubmission({
    initialFormData:  fetchedState ?? initialFormData,
    currentFormData: dialogState,
    additionalValidation: checkIsValid,
    }),
    dismissDialogOnActionFailed: true,
    dialogWidth: TabDialogWidth.width500px,
    dialogBodyStyle: {padding: '16px 18px'},
    initiallyFocusedElement: (dialog) => dialog.querySelector(`#${CERTIFICATION_LABEL_VALUE_DIALOG_MESSAGE_ID}`),
  };
}

function fetchInitialState(dialogType: DialogType) {
  const siteLuid = SessionSiteSelectors.siteLuid(getAppStore().getState());
  const category = dialogType;
  return makeFetchInitialStateForLabelValues<LabelValueDialogBodyProps, LabelValueDialogState>({
    category,
    siteLuid: siteLuid,
    transform: (t) => ({
      labelValueName: t.name,
      category: t.category,
      description: t.description,
      elevatedDefault: t.elevatedDefault
    }),
  });
}

function convertCategoryToType(category: string) {
  switch (category) {
    case 'WARNING':
      return 'warning';
    case 'CERTIFICATION':
      return 'certification';
    default: {
      throw Error('This type is not supported yet');
    }
  }
}

